import type { PrescreenFields } from '../../agent-profile/contact-info/contact-agent/prescreen-form'
import type { ScheduleTourFormValues } from './schedule-tour-form/types'
import type { Choice } from 'components/common/organisms/limited-checkbox-group/types'
import type { ChangeEvent, ChangeEventHandler } from 'react'
import type { UserProfile } from 'store/users/types'

import { range } from 'lodash'
import moment from 'moment'
import { useMemo, useState, useEffect } from 'react'

import { LimitedCheckBoxGroup } from 'components/common/organisms/limited-checkbox-group'

import { EmailModalFooter } from '../../agent-profile/contact-info/contact-agent/email-modal/footer'
import { PrescreenForm } from '../../agent-profile/contact-info/contact-agent/prescreen-form'

import { REQUEST_TOUR_SUBTITLE } from './const'
import { ScheduleTourSubTitle } from './listing-schedule-tour.styled'
import { ScheduleTourForm } from './schedule-tour-form'

export interface ScheduleTourFormData {
  scheduleTourData: ScheduleTourFormValues
  selectedDates: string[]
}

interface FormProps {
  defaultMessage: string
  onSubmit: (formData: ScheduleTourFormData) => void
  profile?: UserProfile
  formData?: ScheduleTourFormData
  isMobile: boolean
}
interface OptionalFormProps {
  onClickPrevious: () => void
  onSubmit: (formData: PrescreenFields) => void
}

const ScheduleTourFormWrapper: React.FC<FormProps> = ({
  defaultMessage,
  onSubmit,
  profile,
  formData,
  isMobile,
}) => {
  const [selectedDates, setSelectedDates] = useState([] as string[])
  useEffect(() => {
    if (formData) {
      setSelectedDates(formData.selectedDates)
    }
  }, [formData])
  const selectionLimit = 3
  const choices: Choice[] = useMemo(
    () =>
      range(1, 7).map((dayNumber) => {
        const date = moment().add(dayNumber, 'days')
        return {
          id: `date${dayNumber}`,
          name: `date${dayNumber}`,
          value: date.format('ddd, MMM D'),
          title: date.format('ddd'),
          subtitle: date.format('MMM D'),
        }
      }),
    [],
  )

  const handleCheckBoxOnChange: ChangeEventHandler<HTMLInputElement> = (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedDates((prevDates) => {
      const newDate = e.target.value
      if (e.target.checked) {
        if (selectedDates.length < selectionLimit && !selectedDates.includes(newDate)) {
          return prevDates.concat(newDate)
        }
        return prevDates
      }
      return prevDates.filter((date) => date !== newDate)
    })
  }

  const handleSubmit = (data: ScheduleTourFormValues) => {
    onSubmit({
      scheduleTourData: data,
      selectedDates,
    })
  }

  return (
    <>
      <ScheduleTourSubTitle>{REQUEST_TOUR_SUBTITLE}</ScheduleTourSubTitle>
      <LimitedCheckBoxGroup
        choices={choices}
        limit={selectionLimit}
        values={selectedDates}
        onChange={handleCheckBoxOnChange}
        label="Select a few dates"
        required
        ascent="(up to 3)"
        $mb="24"
      />
      <ScheduleTourForm
        handleSubmit={handleSubmit}
        defaultMessage={defaultMessage}
        profile={profile}
        isDateSelected={selectedDates?.length > 0}
        formData={formData?.scheduleTourData}
      />
      {isMobile && <EmailModalFooter hasTerm={false} />}
    </>
  )
}

const ScheduleTourOptionalFormWrapper: React.FC<OptionalFormProps> = ({
  onSubmit,
  onClickPrevious,
}) => (
  <>
    <PrescreenForm
      data-testid="schedule-preescreen-form"
      onSubmit={onSubmit}
      onClickPrevious={onClickPrevious}
      submitText="Request a Tour"
    />
    <EmailModalFooter hasTerm={false} />
  </>
)

export { ScheduleTourFormWrapper, ScheduleTourOptionalFormWrapper }
